import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabaseApi, Project } from '../services/supabase';
import { 
  FolderOpen, 
  ArrowLeft, 
  Edit, 
  ExternalLink,
  Github,
  Globe,
  Calendar,
  DollarSign,
  User,
  Tag,
  Image as ImageIcon,
  Loader
} from 'lucide-react';
import styled from 'styled-components';

const ProjectViewContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #667eea;
`;

const MainContent = styled.div`
  display: flex;
  min-height: calc(100vh - 70px);
`;

const Sidebar = styled.aside`
  width: 250px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 24px 0;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8fafc;
    color: #667eea;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #1e293b;
`;

const EditButton = styled.button`
  background: #667eea;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
`;

const ProjectContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 32px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 4rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProjectTitle = styled.h2`
  font-size: 2rem;
  font-weight: bold;
  color: #1e293b;
  margin-bottom: 16px;
`;

const ProjectDescription = styled.p`
  color: #64748b;
  font-size: 16px;
  line-height: 1.7;
  margin-bottom: 24px;
`;

const ProjectShortDescription = styled.p`
  color: #374151;
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 24px;
  font-weight: 500;
`;

const TagsSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ProjectTag = styled.span`
  background: #e0e7ff;
  color: #3730a3;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
`;

const LinksSection = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #f8fafc;
  color: #374151;
  text-decoration: none;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f0f4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667eea;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-bottom: 2px;
`;

const InfoValue = styled.div`
  font-weight: 500;
  color: #1e293b;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#dcfce7';
      case 'in-progress': return '#fef3c7';
      case 'planning': return '#dbeafe';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#166534';
      case 'in-progress': return '#92400e';
      case 'planning': return '#1e40af';
      default: return '#374151';
    }
  }};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #dc2626;
`;

const ProjectView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      fetchProject(id);
    }
  }, [id]);

  const fetchProject = async (projectId: string) => {
    try {
      setLoading(true);
      const data = await supabaseApi.getProject(projectId);
      if (data) {
        setProject(data);
      } else {
        setError('Project not found');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ProjectViewContainer>
        <Header>
          <HeaderContent>
            <Logo>🚀 Portfolio Admin</Logo>
          </HeaderContent>
        </Header>
        <MainContent>
          <Sidebar>
            <NavItem onClick={() => navigate('/')}>
              <FolderOpen size={20} />
              Dashboard
            </NavItem>
            <NavItem onClick={() => navigate('/projects')}>
              <FolderOpen size={20} />
              Projects
            </NavItem>
            <NavItem onClick={() => navigate('/categories')}>
              <FolderOpen size={20} />
              Categories
            </NavItem>
          </Sidebar>
          <Content>
            <LoadingState>
              <Loader size={20} />
              Loading project...
            </LoadingState>
          </Content>
        </MainContent>
      </ProjectViewContainer>
    );
  }

  if (error || !project) {
    return (
      <ProjectViewContainer>
        <Header>
          <HeaderContent>
            <Logo>🚀 Portfolio Admin</Logo>
          </HeaderContent>
        </Header>
        <MainContent>
          <Sidebar>
            <NavItem onClick={() => navigate('/')}>
              <FolderOpen size={20} />
              Dashboard
            </NavItem>
            <NavItem onClick={() => navigate('/projects')}>
              <FolderOpen size={20} />
              Projects
            </NavItem>
            <NavItem onClick={() => navigate('/categories')}>
              <FolderOpen size={20} />
              Categories
            </NavItem>
          </Sidebar>
          <Content>
            <ErrorState>
              <div>{error || 'Project not found'}</div>
              <button
                onClick={() => navigate('/projects')}
                style={{
                  marginTop: '16px',
                  padding: '12px 24px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Back to Projects
              </button>
            </ErrorState>
          </Content>
        </MainContent>
      </ProjectViewContainer>
    );
  }

  return (
    <ProjectViewContainer>
      <Header>
        <HeaderContent>
          <Logo>🚀 Portfolio Admin</Logo>
        </HeaderContent>
      </Header>

      <MainContent>
        <Sidebar>
          <NavItem onClick={() => navigate('/')}>
            <FolderOpen size={20} />
            Dashboard
          </NavItem>
          <NavItem onClick={() => navigate('/projects')}>
            <FolderOpen size={20} />
            Projects
          </NavItem>
          <NavItem onClick={() => navigate('/categories')}>
            <FolderOpen size={20} />
            Categories
          </NavItem>
        </Sidebar>

        <Content>
          <PageHeader>
            <BackButton onClick={() => navigate('/projects')}>
              <ArrowLeft size={20} />
            </BackButton>
            <PageTitle>Project Details</PageTitle>
            <EditButton onClick={() => navigate(`/edit-project/${project.id}`)}>
              <Edit size={16} />
              Edit Project
            </EditButton>
          </PageHeader>

          <ProjectContent>
            <MainSection>
              <ProjectImage>
                {project.image_url ? (
                  <img src={project.image_url} alt={project.title} />
                ) : (
                  <ImageIcon size={80} />
                )}
              </ProjectImage>

              <ProjectTitle>{project.title}</ProjectTitle>
              
              {project.short_description && (
                <ProjectShortDescription>{project.short_description}</ProjectShortDescription>
              )}
              
              <ProjectDescription>{project.description}</ProjectDescription>

              {project.technologies && project.technologies.length > 0 && (
                <TagsSection>
                  <SectionTitle>Technologies Used</SectionTitle>
                  <TagsContainer>
                    {project.technologies.map((tech, index) => (
                      <ProjectTag key={index}>{tech}</ProjectTag>
                    ))}
                  </TagsContainer>
                </TagsSection>
              )}

              {project.features && project.features.length > 0 && (
                <TagsSection>
                  <SectionTitle>Key Features</SectionTitle>
                  <TagsContainer>
                    {project.features.map((feature, index) => (
                      <ProjectTag key={index}>{feature}</ProjectTag>
                    ))}
                  </TagsContainer>
                </TagsSection>
              )}

              <LinksSection>
                {project.project_url && (
                  <LinkButton href={project.project_url} target="_blank" rel="noopener noreferrer">
                    <Globe size={16} />
                    Live Demo
                  </LinkButton>
                )}
                
                {project.github_url && (
                  <LinkButton href={project.github_url} target="_blank" rel="noopener noreferrer">
                    <Github size={16} />
                    GitHub
                  </LinkButton>
                )}
                
                {project.demo_url && (
                  <LinkButton href={project.demo_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={16} />
                    Demo
                  </LinkButton>
                )}
              </LinksSection>
            </MainSection>

            <SideSection>
              <InfoCard>
                <SectionTitle>Project Information</SectionTitle>
                
                <InfoItem>
                  <InfoIcon>
                    <Tag size={20} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Category</InfoLabel>
                    <InfoValue>{project.category?.name || 'Uncategorized'}</InfoValue>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon>
                    <User size={20} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Client</InfoLabel>
                    <InfoValue>{project.client || 'N/A'}</InfoValue>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon>
                    <Calendar size={20} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Duration</InfoLabel>
                    <InfoValue>{project.duration || 'N/A'}</InfoValue>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon>
                    <DollarSign size={20} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Budget</InfoLabel>
                    <InfoValue>{project.budget || 'N/A'}</InfoValue>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon>
                    <Tag size={20} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Status</InfoLabel>
                    <InfoValue>
                      <StatusBadge status={project.status}>
                        {project.status}
                      </StatusBadge>
                    </InfoValue>
                  </InfoContent>
                </InfoItem>
              </InfoCard>

              <InfoCard>
                <SectionTitle>Timeline</SectionTitle>
                <InfoItem>
                  <InfoIcon>
                    <Calendar size={20} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Created</InfoLabel>
                    <InfoValue>
                      {new Date(project.created_at).toLocaleDateString()}
                    </InfoValue>
                  </InfoContent>
                </InfoItem>
                
                <InfoItem>
                  <InfoIcon>
                    <Calendar size={20} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Last Updated</InfoLabel>
                    <InfoValue>
                      {new Date(project.updated_at).toLocaleDateString()}
                    </InfoValue>
                  </InfoContent>
                </InfoItem>
              </InfoCard>
            </SideSection>
          </ProjectContent>
        </Content>
      </MainContent>
    </ProjectViewContainer>
  );
};

export default ProjectView;
